import './datasourceSelectionConfig.less';
import { GridStore } from 'controls/grid/gridStore';
import { computed, makeAutoObservable } from 'mobx';
import { FieldType, RulesConfiguration } from 'controls/queryBuilder/ruleDefinition';
import { GridColumnConfig } from 'controls/grid/gridColumnConfig';
import moment from 'moment';
import { ApplicationState } from 'framework/applicationState';
import { observer } from 'mobx-react';
import React, { MouseEvent } from 'react';
import { Section } from 'controls/react/layout/section';
import { Toolbar, ToolbarItemPosition } from 'controls/react/layout/toolbar';
import { Grid } from 'controls/grid/grid';
import { RemoteDataProvider } from 'controls/grid/remoteDataProvider';
import { TimePeriodType } from 'controls/react/form/timePeriodType';
import { GridSelectionType } from 'controls/grid/gridConfiguration';
import { apiFetch, ApiRequest, ApiResponse, PagedList } from 'framework/api';
import { AntCheckbox } from 'controls/react/ant/antCheckbox';
import b_ from 'b_';
import { BoxView } from 'controls/react/layout/boxView';
import { AntButton } from 'controls/react/ant/antButton';
import { MobxManager } from 'framework/mobx-integration';
import { FormEntryNew } from 'controls/react/form/formEntryNew';
import { GridDataItem } from 'controls/grid/gridDataItem';
import { GridSelectionMode } from 'controls/grid/gridSelection';
import { DataSourceDataItem, getDataSourceData } from 'controls/designer/features/widgets/datasources/api';
import { translator } from 'core/localization';
import { PSEUDO_FULL_RESPONSE } from 'controls/designer/features/widgets/datasources/shared/constants';
import { uniq } from 'lodash';
import { GridSearchInput, GridSearchInputProps } from 'controls/grid/gridSearchInput';
import { DataSizeIndicator } from 'controls/grid/dataSizeIndicator';
import { ColumnSelectorCountIndicator } from 'controls/designer/features/widgets/datasources/components/columnSelectorCountIndicator';

export enum SelectionMode {
	Columns = 1,
	Rows = 2,
	CategoryColumns,
}

export type DatasourceSelectionConfigProps = {
	accountId: string;
	dataSourceId: string;
	selectionMode?: SelectionMode;
	fieldType?: FieldType;
	categoryColumns?: string[];
	value?: string[];
	onChange?: (value: string[]) => void;
	onCancel?: () => void;
	requestId: string;
	allOptions?: string[];
	pivotSettings?: {
		pivotMatrix?: boolean;
		pivotColumns?: string[];
		pivotRows?: string[];
		pivotValues?: string[];
	};
};

const gridCssBlock = b_.with('ceeview-grid');
export const i18n = translator({
	Rows: { no: 'Rader' },
	Columns: { no: 'Kolonner' },
	Values: { no: 'Verdier' },
	ROWS_WARNING_MESSAGE: {
		en: 'The number of rows is limited to {0}, while the datasource contains {1} rows in total. This limit is applied to improve performance.',
		no: 'Antall rader er begrenset til {0}, mens datakilden totalt inneholder {1} rader. Denne begrensningen er satt for å forbedre ytelsen.',
	},
	'Search columns': {
		no: 'Søk kolonner',
	},
	'Search rows': {
		no: 'Søk rader',
	},
});

class DatasourceSelectionConfigStore {
	gridStore: GridStore<DataSourceDataItem>;
	private selectedItems: string[] = [];
	title: string;
	infoMessage: string;
	columnsWarningMessage: string;
	rowsWarningMessage: string;
	filtersConfig: RulesConfiguration;
	searchConfig: Partial<GridSearchInputProps<DataSourceDataItem>>;
	private options: string[] = [];
	mobx = new MobxManager();

	get atLeastOneSelected() {
		if (this.props.selectionMode == SelectionMode.Rows) {
			return this.gridStore.selection.dataset.items.length > 0;
		}
		return this.selectedItems.length > 0;
	}

	get filteredOptions() {
		return this.options.filter((x) => this.gridStore.columns.visible.map((x) => x.field).includes(x));
	}

	get allChecked() {
		return this.selectedItems.length == this.filteredOptions.length;
	}

	set allChecked(val: boolean) {
		if (val) {
			this.selectedItems = [...this.filteredOptions];
		} else {
			this.selectedItems = [];
		}
	}

	get selectionMode() {
		return this.props.selectionMode;
	}

	get indeterminate() {
		return this.selectedItems.length > 0 && this.selectedItems.length < this.filteredOptions.length;
	}

	constructor(private props: DatasourceSelectionConfigProps) {
		makeAutoObservable(this, {
			atLeastOneSelected: computed,
			allChecked: computed,
			selectionMode: computed,
			indeterminate: computed,
		});
		this.mobx.reaction(
			() => this.gridStore?.dataProvider?.initialized,
			(initialized) => {
				if (initialized && this.props.selectionMode == SelectionMode.Rows) {
					const selected = this.gridStore.dataProvider.data.filter((x) =>
						(this.props.value ?? []).includes(this.props.categoryColumns.map((y) => x.data[y]).join(' ')),
					);
					selected.forEach((x) => this.gridStore.selection.toggleSelected(x));
				}
			},
		);
	}

	init = async () => {
		this.filtersConfig = (await apiFetch(this.getDataSourceFiltersConfiguration())).data;

		this.initCategorySelectionMode();
		this.initRowSelectionMode();
		this.initColumnSelectionMode();

		this.gridStore = new GridStore<DataSourceDataItem>({
			columns: this.getColumns(this.filtersConfig),
			dataProvider: new RemoteDataProvider({
				dataSource: this.getDataRequest(),
				filtersConfiguration: this.filtersConfig,
				onBatchLoaded: this.onBatchLoaded,
			}),
			defaults: {
				payload: {
					timePeriod: {
						period: TimePeriodType.Last24Hours,
					},
				},
			},
			selection: this.props.selectionMode == SelectionMode.Rows ? GridSelectionType.Many : GridSelectionType.None,
		});
		this.searchConfig =
			this.selectionMode == SelectionMode.Rows
				? {
						placeholder: i18n('Search rows'),
					}
				: {
						searchSource: 'columns',
						placeholder: i18n('Search columns'),
					};
	};

	onBatchLoaded = (response: ApiResponse<PagedList<GridDataItem<DataSourceDataItem>>>) => {
		if (response.data.warning) {
			this.columnsWarningMessage = response.data.warning;
		}
		if (
			response.data.total > response.data.visible &&
			response.data.visible < PSEUDO_FULL_RESPONSE &&
			!this.gridStore.state.searchString &&
			!this.gridStore.columns.searchString
		) {
			this.rowsWarningMessage = i18n('ROWS_WARNING_MESSAGE', response.data.visible, response.data.total);
		}
	};

	initCategorySelectionMode = () => {
		if (this.props.selectionMode != SelectionMode.CategoryColumns) {
			return;
		}
		this.selectedItems = this.props.value ?? [];
		this.options = Object.keys(this.filtersConfig).filter((x) => x.toUpperCase() != 'ID');
		this.title = i18n('Rows');
	};

	initRowSelectionMode = () => {
		if (this.props.selectionMode != SelectionMode.Rows) {
			return;
		}

		this.title = i18n('Values');
		this.options = this.props.allOptions;
	};

	initColumnSelectionMode = () => {
		if (this.props.selectionMode != SelectionMode.Columns) {
			return;
		}

		this.title = i18n('Columns');
		this.selectedItems = this.props.value
			? [...this.props.value]
			: Object.keys(this.filtersConfig).filter((x) => this.filtersConfig[x].type == this.props.fieldType);

		this.options = Object.keys(this.filtersConfig).filter(
			(x) => (!this.props.fieldType || this.filtersConfig[x].type == this.props.fieldType) && x.toUpperCase() != 'ID',
		);
	};

	getDataRequest = () => {
		const dataRequest = getDataSourceData({
			...this.props,
			...this.props.pivotSettings,
			categoryColumns: [],
			dataColumns: [],
			dataRows: [],
			pivotData: undefined,
		});
		const payload = { ...(dataRequest.payload as object) } as {
			filter: unknown;
			sort: string[];
			skip: number;
			take: number;
		};
		payload.filter = {};
		payload.sort = [];
		payload.skip = 0;
		payload.take = PSEUDO_FULL_RESPONSE;
		dataRequest.payload = payload;
		return dataRequest;
	};

	getDataSourceFiltersConfiguration = () => {
		return new ApiRequest<RulesConfiguration>({
			url: `dataSources/${this.props.dataSourceId}/ruleConfiguration`,
			accountId: this.props.accountId,
			accountBased: true,
			method: 'POST',
			payload: this.props.pivotSettings ?? {},
		});
	};

	getColumns(rules: RulesConfiguration) {
		return Object.keys(rules)
			.filter((x) => x != 'id')
			.map((columnName) => {
				const column: GridColumnConfig<DataSourceDataItem> = {
					field: columnName,
					// hack from Bernt
					title: rules[columnName].label,
					rendererHeader: () => {
						const label = rules[columnName].label || 'Default';
						switch (this.props.selectionMode) {
							case SelectionMode.CategoryColumns: {
								return (
									<AntCheckbox
										title={label}
										onClick={this.onHeaderCheckboxClick}
										value={this.selectedItems.includes(columnName)}
										onChange={(value) => this.toggleColumn(columnName, value)}
									>
										{label}
									</AntCheckbox>
								);
							}
							case SelectionMode.Columns: {
								return (
									<>
										{(!this.props.fieldType || this.filtersConfig[columnName].type == this.props.fieldType) && (
											<AntCheckbox
												title={label}
												onClick={this.onHeaderCheckboxClick}
												value={this.selectedItems.includes(columnName)}
												onChange={(value) => this.toggleColumn(columnName, value)}
											>
												{label}
											</AntCheckbox>
										)}
										{this.props.fieldType && this.filtersConfig[columnName].type != this.props.fieldType && (
											<>{label}</>
										)}
									</>
								);
							}
						}
						return <>{label}</>;
					},
					renderer: (item) => {
						if (rules[columnName].type == FieldType.Number) {
							try {
								return (item[columnName] as number)?.toFixed(0) ?? '';
							} catch (e) {
								console.error(`value: ${item[columnName]}`, e);
							}
						}
						if (rules[columnName].type == FieldType.Date && !!item[columnName]) {
							return moment(item[columnName]).format(ApplicationState.momentDateTimeFormat);
						}
						return item[columnName] ?? '';
					},
					className: () => gridCssBlock('cell', { selected: this.selectedItems.includes(columnName) }),
				};

				return column;
			});
	}

	onHeaderCheckboxClick = (ev: MouseEvent<HTMLElement>) => {
		ev.stopPropagation();
	};

	toggleColumn = (rowHeader: string, checked: boolean) => {
		if (checked) {
			this.selectedItems.push(rowHeader);
		} else {
			const index = this.selectedItems.findIndex((x) => x.toUpperCase() == rowHeader.toUpperCase());
			this.selectedItems.splice(index, 1);
		}
	};

	onUpdate = () => {
		if (!this.props.onChange) {
			return;
		}
		if (this.props.selectionMode == SelectionMode.Rows) {
			const selectedItems = this.gridStore.selection.dataset.items.filter((item) => {
				if (this.gridStore.selection.mode == GridSelectionMode.Exclude) {
					return !this.gridStore.selection.ids.includes(item.id);
				}
				return this.gridStore.selection.ids.includes(item.id);
			});
			const uiSelectedCategories = uniq(
				selectedItems.map((item) => this.props.categoryColumns.map((x) => item.data[x]).join(' ')),
			);
			this.props.onChange(this.options.filter((x) => uiSelectedCategories.includes(x)));
			return;
		}
		this.props.onChange(this.selectedItems);
	};

	closeRowsWarning = () => {
		this.rowsWarningMessage = undefined;
	};
	closeColumnsWarning = () => {
		this.columnsWarningMessage = undefined;
	};

	destroy() {
		this.gridStore.destroy();
	}
}

export const DatasourceSelectionConfig = observer(
	class InnerModal extends React.Component<DatasourceSelectionConfigProps> {
		store: DatasourceSelectionConfigStore;
		constructor(props: DatasourceSelectionConfigProps) {
			super(props);
			this.store = new DatasourceSelectionConfigStore(props);
		}

		async componentDidMount() {
			await this.store.init();
		}

		componentWillUnmount() {
			this.store.destroy();
		}

		render() {
			if (!this.store.gridStore?.selfInitialized) {
				return null;
			}

			return (
				<Section appearance="none" height={'fit'}>
					<Toolbar title={this.store.title} containerClass={gridCssBlock('toolbar')}>
						{this.store.selectionMode != SelectionMode.Rows && (
							<FormEntryNew model={this.store} modelField={'allChecked'} position={ToolbarItemPosition.AFTER_TITLE}>
								<AntCheckbox indeterminate={this.store.indeterminate}>
									{this.store.allChecked ? i18n('Deselect_all') : i18n('Select_all')}
								</AntCheckbox>
							</FormEntryNew>
						)}
						{this.store.selectionMode == SelectionMode.Rows && (
							<DataSizeIndicator position={ToolbarItemPosition.AT_THE_END} store={this.store.gridStore} />
						)}
						{this.store.selectionMode != SelectionMode.Rows && (
							<ColumnSelectorCountIndicator position={ToolbarItemPosition.AT_THE_END} store={this.store.gridStore} />
						)}
						<GridSearchInput
							position={ToolbarItemPosition.AT_THE_END}
							store={this.store.gridStore}
							{...this.store.searchConfig}
						/>
					</Toolbar>
					{!!this.store.infoMessage && <BoxView type={'info-2'}>{this.store.infoMessage}</BoxView>}
					{!!this.store.rowsWarningMessage && (
						<BoxView type={'warning'} onClick={this.store.closeRowsWarning}>
							{this.store.rowsWarningMessage}
						</BoxView>
					)}
					{!!this.store.columnsWarningMessage && (
						<BoxView type={'warning'} onClick={this.store.closeColumnsWarning}>
							{this.store.columnsWarningMessage}
						</BoxView>
					)}
					<Grid store={this.store.gridStore} />
					<Toolbar>
						<AntButton
							type={'primary'}
							onClick={this.store.onUpdate}
							disabled={!this.store.atLeastOneSelected}
							position={ToolbarItemPosition.AT_THE_END}
						>
							{i18n('Update')}
						</AntButton>
						<AntButton onClick={this.props.onCancel} position={ToolbarItemPosition.AT_THE_END}>
							{i18n('Cancel')}
						</AntButton>
					</Toolbar>
				</Section>
			);
		}
	},
);
